/*
 * @Copyright 2024-2025. Institute for Future Intelligence, Inc.
 */

import { Atom } from './Atom.ts';
import { RadialBond } from './RadialBond.ts';
import { BeadModel, MoleculeInterface } from '../types.ts';
import { applyBeadBondedTerms } from './beadModel.ts';
import { Vector3 } from 'three';
import { AngularBond } from './AngularBond.ts';
import { TorsionalBond } from './TorsionalBond.ts';
import { applyTorsionParameters } from './torsionParamLookup.ts';
import { ImproperBond } from './ImproperBond.ts';
import { applyMmffParameters, applyMmffTorsionParameters, buildMmffImpropers } from './mmffParamLookup.ts';
import { MolecularViewerStyle } from '../view/displayOptions.ts';

export class Molecule implements MoleculeInterface {
  name: string;
  atoms: Atom[];
  radialBonds: RadialBond[];
  angularBonds: AngularBond[];
  torsionalBonds: TorsionalBond[];
  improperBonds: ImproperBond[];
  center: Vector3;

  url?: string;
  data?: string;
  autoBond?: boolean;
  internal?: boolean;
  invisible?: boolean;
  excluded?: boolean;
  multipleResidues?: boolean;
  style?: MolecularViewerStyle;
  overlayStyle?: MolecularViewerStyle | null;
  metadata?: Record<string, unknown>;
  // Per-atom partial charges (parallel to the SDF atom block). Read by the
  // ESP renderer in dynamicsViewer to color atoms by polarity.
  atomCharges?: number[];

  // Polymer Builder metadata. Populated by buildChainModal after polymerBuilder
  // returns; consumed by the Polymer analysis panel for end-to-end distance,
  // radius of gyration, and persistence-length computation. Indices are into
  // this molecule's own `atoms` array. Optional — undefined for any molecule
  // that wasn't produced by buildPolymerChain.
  polymerChainHeadAtomIndex?: number;
  polymerChainTailAtomIndex?: number;
  // Coarse-grained molecules only (beadModel.ts): the bead types and the
  // per-atom assignment that override the placeholder elements in `data`.
  beads?: BeadModel;

  constructor(name: string, atoms: Atom[]) {
    this.name = name;
    this.atoms = atoms;
    this.radialBonds = [];
    this.angularBonds = [];
    this.torsionalBonds = [];
    this.improperBonds = [];
    this.center = new Vector3();
  }

  // for molecule serialized from Firestore, set full to be true
  static clone(molecule: Molecule, full?: boolean): Molecule {
    const map = new Map<number, Atom>();
    const newAtoms: Atom[] = [];
    for (const [i, a] of molecule.atoms.entries()) {
      const clone = Atom.clone(a, full);
      map.set(i, clone);
      newAtoms.push(clone);
    }
    const newRadialBonds: RadialBond[] = [];
    if (molecule.radialBonds && molecule.radialBonds.length > 0) {
      for (const b of molecule.radialBonds) {
        const a1 = map.get(b.atom1.index);
        const a2 = map.get(b.atom2.index);
        // Preserve the equilibrium length and bond order: clone used to drop
        // both, which discarded any non-default stiffness and (for order) left
        // the MMFF-lite typer below blind to π bonds.
        if (a1 && a2) {
          const nb = new RadialBond(a1, a2, b.length);
          nb.type = b.type;
          newRadialBonds.push(nb);
        }
      }
    }
    const newAngularBonds: AngularBond[] = [];
    if (molecule.angularBonds && molecule.angularBonds.length > 0) {
      for (const b of molecule.angularBonds) {
        const a1 = map.get(b.atom1.index);
        const a2 = map.get(b.atom2.index);
        const a3 = map.get(b.atom3.index);
        if (a1 && a2 && a3) newAngularBonds.push(new AngularBond(a1, a2, a3));
      }
    }
    const newTorsionalBonds: TorsionalBond[] = [];
    if (molecule.torsionalBonds && molecule.torsionalBonds.length > 0) {
      for (const b of molecule.torsionalBonds) {
        const a1 = map.get(b.atom1.index);
        const a2 = map.get(b.atom2.index);
        const a3 = map.get(b.atom3.index);
        const a4 = map.get(b.atom4.index);
        // Carry the source's as-built dihedral so the torsion pack below pins
        // the clone's minima where the original's were, not at whatever
        // conformation the atoms are in right now (a plain Firestore object
        // has no builtDihedral → fall back to the geometry).
        if (a1 && a2 && a3 && a4) newTorsionalBonds.push(new TorsionalBond(a1, a2, a3, a4, b.builtDihedral));
      }
      // Apply polymer-relevant OPLS-style FF pack to the cloned torsions;
      // quadruples not in the table fall back to engine defaults.
      applyTorsionParameters(newTorsionalBonds);
    }
    // Re-apply the MMFF-lite CHON bond/angle pack to the clone (the same way
    // torsions are re-applied above), so cloned molecules carry the same
    // type-resolved force constants instead of reverting to the default k=10.
    applyMmffParameters(newAtoms, newRadialBonds, newAngularBonds);
    // Likewise re-apply the CHON torsion pack (height + periodicity, phase
    // pinned to the as-built dihedral) so clones don't revert to the stiff k=1.
    applyMmffTorsionParameters(newAtoms, newRadialBonds, newTorsionalBonds);
    const mol = new Molecule(molecule.name, newAtoms);
    mol.radialBonds = newRadialBonds;
    mol.angularBonds = newAngularBonds;
    mol.torsionalBonds = newTorsionalBonds;
    // Regenerate out-of-plane (improper) terms from the cloned topology (like
    // angular/torsional bonds, they're derived, not copied).
    mol.improperBonds = buildMmffImpropers(newAtoms, newRadialBonds);
    mol.style = molecule.style;
    mol.overlayStyle = molecule.overlayStyle;
    mol.multipleResidues = molecule.multipleResidues;
    // copy the data if the molecule is not from the internal database
    mol.data = molecule.data;
    mol.autoBond = molecule.autoBond;
    // Per-atom partial charges are aligned with the SDF atom block, which
    // we copied above — clone the array so the ESP renderer continues to
    // work for pasted/duplicated molecules.
    if (molecule.atomCharges) mol.atomCharges = molecule.atomCharges.slice();
    // Polymer endpoints: bare integer indices, so a plain copy is enough. We
    // copy them unconditionally — Firestore round-trips drop class identity
    // but preserve plain fields on the data record, and Molecule.clone is
    // the rehydration path (see ModelUtil.reconstructMoleculesFromFirestore).
    if (molecule.polymerChainHeadAtomIndex !== undefined) {
      mol.polymerChainHeadAtomIndex = molecule.polymerChainHeadAtomIndex;
    }
    if (molecule.polymerChainTailAtomIndex !== undefined) {
      mol.polymerChainTailAtomIndex = molecule.polymerChainTailAtomIndex;
    }
    // A coarse-grained molecule: re-apply its bead model to the fresh atoms,
    // whose constructor just read the PLACEHOLDER element's mass / σ / ε (a
    // molecule back from Firestore has its atoms' bead fields stripped).
    if (molecule.beads) {
      mol.beads = molecule.beads;
      for (let i = 0; i < newAtoms.length; i++) {
        const t = mol.beads.types[mol.beads.typeOfAtom[i]];
        if (t) newAtoms[i].setBead(t);
      }
      // The bonded terms too: the typers above just gave the bonds the
      // placeholder element's constants (beadModel.ts, applyBeadBondedTerms).
      applyBeadBondedTerms(mol, mol.beads);
    }
    return mol;
  }

  updateCenter() {
    if (this.atoms.length === 0) return;
    this.center.set(0, 0, 0);
    for (const a of this.atoms) {
      this.center.add(a.position);
    }
    this.center.multiplyScalar(1 / this.atoms.length);
  }

  setCenter(point: Vector3) {
    this.updateCenter();
    const diff = new Vector3().subVectors(point, this.center);
    for (const a of this.atoms) {
      a.position.add(diff);
    }
    this.center.copy(point);
  }
}
